import { useQuery } from "@apollo/client/react";
import { Box, Grid2 as Grid, Skeleton } from "@mui/material";
// eslint-disable-next-line no-restricted-imports
import { Carousel, InlineError, Typography, useLocalStorage } from "@pmag/ui-components";
import { type GQLRecentlyViewedQuery, type GQLRecentlyViewedQueryVariables } from "@src/graphql.generated";
import { RecentlyViewedCard } from "@src/pages/landingPage/components/recentlyViewed/components/card/RecentlyViewedCard";
import { recentlyViewedQuery } from "@src/pages/landingPage/components/recentlyViewed/RecentlyViewed.gql";
import {
    sortModelsByViewed,
    splitModelsIntoChunksPerPage,
    useResolveModelsPerPage,
} from "@src/pages/landingPage/components/recentlyViewed/RecentlyViewed.utils";
import { type FunctionComponent } from "react";
import { FormattedMessage } from "react-intl";
import * as yup from "yup";

import { CardContainer, CarouselContainer } from "./RecentlyViewed.styles";

export const dataTestId = {
    container: "sparePartsFinder.landingPage.recentlyViewed.container",
    card: "sparePartsFinder.landingPage.recentlyViewed.card",
};

export const RecentlyViewed: FunctionComponent = () => {
    const numberOfModelsPerPage = useResolveModelsPerPage();
    const [viewedModels] = useLocalStorage<string[]>("recentlyViewedModels", [], yup.array().of(yup.string().required()).required());
    const { data, loading, error } = useQuery<GQLRecentlyViewedQuery, GQLRecentlyViewedQueryVariables>(recentlyViewedQuery, {
        variables: { modelIds: viewedModels },
        skip: viewedModels.length <= 0,
    });

    if (viewedModels.length <= 0) return null;
    if (error) return <InlineError />;

    const sortedModels = sortModelsByViewed(viewedModels, data?.models.nodes);
    const modelChunks = sortedModels ? splitModelsIntoChunksPerPage(sortedModels, numberOfModelsPerPage) : [];
    // eslint-disable-next-line no-console
    console.log(modelChunks);

    return (
        <Box data-testid={dataTestId.container}>
            <Box mb={3}>
                <Typography variant="label-02">
                    <FormattedMessage id="sparePartsFinder.landingPage.recentlyViewed.title" defaultMessage="Your recently viewed models" />
                </Typography>
            </Box>

            <CarouselContainer>
                <Carousel>
                    {loading && data === undefined ? (
                        <CardContainer container columnGap={3} columns={3} wrap="nowrap">
                            {Array(3)
                                .fill(0)
                                .map((_, i) => (
                                    <Grid key={`loading-${i}`} size={1}>
                                        <Skeleton height={138} width="100%" />
                                    </Grid>
                                ))}
                        </CardContainer>
                    ) : (
                        modelChunks.map((chunk, i) => (
                            <CardContainer key={`slide-${i}`} container columnGap={3} columns={chunk.length} wrap="nowrap">
                                {chunk.map((model, j) => (
                                    <Grid key={`slide-${i}-chunk-${j}`} size={1}>
                                        <RecentlyViewedCard model={model} />
                                    </Grid>
                                ))}
                            </CardContainer>
                        ))
                    )}
                </Carousel>
            </CarouselContainer>
        </Box>
    );
};
